import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";

import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import PublicLanding from "@/components/PublicLanding";
import Loader from "@/components/ui/loader";
import { api } from "@/lib/api";
import { customLandingFor } from "@/orgs";

/* =============================================================================
   Organization landing page, served at /<slug>.

   Two shapes, one route. A partner with a page of its own in src/orgs/ gets
   that page; everyone else gets PublicLanding — the identical component "/"
   uses — differing only by the org record: its name, headline/subheadline,
   logo in the nav, and membership pricing. So a change to the main landing
   reaches every org that has not written its own page.

   Either way the nav, footer, pinned Dark · Gold theme and membership pricing
   are shared, so a partner page can restyle its story but never its plans.

   Public route: anonymous visitors are the primary audience, so nothing here
   requires a session. Dark · Gold is pinned the same way it is on "/" — this
   is a marketing surface, so the app-wide light/dark toggle doesn't apply.
   ========================================================================== */

/** The org record as returned by /orgs/by-slug/{slug}. */
interface Org {
  id: number;
  name: string;
  slug: string;
  is_default: boolean;
  url_path: string;
  headline: string | null;
  subheadline: string | null;
  description: string | null;
  logo_url: string | null;
  website_url: string | null;
  tier_names: string[];
}

interface LookupItem {
  id: number;
  name: string;
}

export default function OrgLandingPage() {
  const { orgSlug = "" } = useParams();

  const [org, setOrg] = useState<Org | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [categories, setCategories] = useState<LookupItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    setOrg(null);
    setNotFound(false);
    api
      .get<Org>(`/orgs/by-slug/${encodeURIComponent(orgSlug)}`)
      .then((data) => {
        if (cancelled) return;
        // The default org lives at "/", not at its slug — one canonical URL per
        // org, so /businesslocating doesn't become a duplicate landing page.
        if (data.is_default) setNotFound(true);
        else setOrg(data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => {
      cancelled = true;
    };
  }, [orgSlug]);

  // Same lookup the main landing's parent does, so the teaser cards can resolve
  // an industry name without a second request per card.
  useEffect(() => {
    let cancelled = false;
    api
      .get<LookupItem[]>("/lookups/categories")
      .then((data) => {
        if (!cancelled) setCategories(data);
      })
      .catch(() => {
        /* Cards fall back to "Business listing" — see categoryName(). */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A partner with a page of its own in src/orgs/ gets it; everyone else falls
  // back to the generic landing. Resolved after the fetch, since the slug in
  // the URL is not trusted until the org record confirms it.
  const CustomLanding = org ? customLandingFor(org.slug) : null;

  // An unknown slug is just a bad URL — hand it to the marketplace rather than
  // showing a dead end. Matches the catch-all route's behaviour.
  if (notFound) return <Navigate to="/" replace />;
  if (!org)
    return (
      <div data-theme="dark" className="min-h-screen bg-background">
        <Loader fullScreen />
      </div>
    );

  return (
    <div data-theme="dark" className="flex min-h-screen flex-col bg-background">
      <Navigation
        logoVariant="dark"
        logoLockup
        showThemeToggle={false}
        coBrand={{ logoUrl: org.logo_url, name: org.name, slug: org.slug }}
      />
      <div className="flex-1">
        {CustomLanding ? (
          <CustomLanding
            org={{
              id: org.id,
              name: org.name,
              slug: org.slug,
              headline: org.headline,
              subheadline: org.subheadline,
              description: org.description,
              logoUrl: org.logo_url,
              websiteUrl: org.website_url,
              tierNames: org.tier_names,
            }}
          />
        ) : (
          <PublicLanding
            categories={categories}
            org={{
              name: org.name,
              slug: org.slug,
              headline: org.headline,
              subheadline: org.subheadline,
              description: org.description,
              tierNames: org.tier_names,
            }}
          />
        )}
      </div>
      <Footer logoVariant="dark" logoLockup />
    </div>
  );
}
